import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Alert, Skeleton } from '@material-ui/lab';
import RefreshIcon from '@material-ui/icons/Refresh';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import moment from 'moment';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Page from 'src/components/Page';
import { useMailOrbitFeature } from 'src/utils/mailorbitFeature';
import {
  getMyWork,
  getMyQueues,
  getQueueWorkItems,
  getStatuses,
  claimConversation,
  lookupWorkOrders,
  isMailOrbitError,
  getMailOrbitErrorKey,
} from 'src/utils/MailOrbitService';
import { formatRelative } from 'src/components/MailOrbit/WorkOrderEmail/format';
import {
  MUTED, LINE, TINT, PAPER_2, WARN, WARN_BG, CRIT, CRIT_BG, OK, IDLE,
} from 'src/components/MailOrbit/WorkOrderEmail/useStyles';
import { openWorkOrder } from 'src/components/MailOrbit/openWorkOrder';
import AvailabilityControl from './AvailabilityControl';
import { useOpenConversation } from './openConversation';

const PAGE_SIZES = [10, 25, 50, 100];
const TABS = ['mine', 'queue'];
const CHIPS = ['all', 'open', 'high'];
const SORTS = ['created', 'priority'];
const SKELETON_ROWS = 5;
const LOOKUP_MAX = 100; // sayfa basina en fazla kimlik (sayfa boyutlari 10-100)

const useStyles = makeStyles(theme => ({
  // Mockup (docs/ux/email-screens-mockup.html): 14 px taban, 1 px çizgiler, 6 px kart, 4 px düğme.
  page: {
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    '& .MuiButton-root': { textTransform: 'none', fontWeight: 500, borderRadius: 4 },
    '& .MuiButton-sizeSmall': { padding: '4px 10px', fontSize: 13 },
    '& .MuiButton-outlined': { borderColor: LINE },
  },
  card: { border: `1px solid ${LINE}`, borderRadius: 6, boxShadow: 'none' },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '10px 16px',
    padding: '0 14px',
    borderBottom: `1px solid ${LINE}`,
  },
  tab: {
    minWidth: 0,
    padding: '8px 14px',
    textTransform: 'none',
    fontSize: 14,
    fontWeight: 500,
    color: MUTED,
    '&.Mui-selected': { color: theme.palette.text.primary },
  },
  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '10px 16px',
    padding: '10px 14px',
    borderBottom: `1px solid ${LINE}`,
  },
  chip: {
    height: 'auto',
    padding: '3px 0',
    fontSize: 13,
    borderRadius: 14,
    border: `1px solid ${LINE}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    '&:hover, &:focus': { backgroundColor: TINT },
  },
  chipOn: {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover, &:focus': { backgroundColor: theme.palette.primary.dark },
  },
  th: {
    fontSize: 12,
    fontWeight: 500,
    color: MUTED,
    padding: '8px 14px',
    backgroundColor: PAPER_2,
    borderBottom: `1px solid ${LINE}`,
    whiteSpace: 'nowrap',
  },
  td: { padding: '10px 14px', fontSize: 14, borderBottom: `1px solid ${LINE}` },
  muted: { color: MUTED, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  pill: {
    height: 'auto',
    padding: '2px 0',
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 12,
    border: `1px solid ${LINE}`,
    backgroundColor: PAPER_2,
    color: MUTED,
  },
  pillUrgent: { backgroundColor: CRIT_BG, color: CRIT, borderColor: 'transparent' },
  pillHigh: { backgroundColor: WARN_BG, color: WARN, borderColor: 'transparent' },
  pager: { color: MUTED, fontSize: 13 },
  crumbs: { marginBottom: 10, fontSize: 13, color: MUTED },
  filters: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    padding: '10px 14px',
    borderBottom: `1px solid ${LINE}`,
  },
  spacer: { flex: 1 },
  tableContainer: { maxHeight: '65vh', margin: 0 },
  row: {
    cursor: 'pointer',
    '&:hover': { backgroundColor: TINT },
    '&:focus': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
      backgroundColor: TINT,
    },
  },
  refreshing: { opacity: 0.55, transition: 'opacity 150ms' },
  subject: {
    display: 'block',
    color: theme.palette.text.primary,
    fontWeight: 400,
    maxWidth: 520,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  sender: {
    display: 'block',
    fontFamily: theme.typography.fontFamily,
    marginTop: 2,
    color: MUTED,
    fontSize: 12,
  },
  dot: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    marginRight: theme.spacing(1),
  },
  workOrderLink: {
    fontSize: 14,
    fontVariantNumeric: 'tabular-nums',
    verticalAlign: 'baseline',
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  },
  closedMark: { marginLeft: 6, fontSize: 12, color: MUTED },
  go: { whiteSpace: 'nowrap', textAlign: 'right', width: 1 },
  chevron: { verticalAlign: 'middle', color: theme.palette.text.secondary },
  empty: { padding: theme.spacing(4, 2), textAlign: 'center' },
}));

// URL sorgusundaki liste durumunu okur; gecersiz degerler varsayilana duser.
const intParam = (params, key, fallback, allowed) => {
  const n = parseInt(params.get(key), 10);
  if (Number.isNaN(n) || n < 0) return fallback;
  return allowed && !allowed.includes(n) ? fallback : n;
};
const enumParam = (params, key, allowed, fallback) => {
  const v = params.get(key);
  return allowed.includes(v) ? v : fallback;
};
const boolParam = (params, key, fallback) => {
  const v = params.get(key);
  if (v === '1') return true;
  if (v === '0') return false;
  return fallback;
};

const MyEmailWorkContent = () => {
  const { t } = useTranslation(['mailorbitWork', 'mailorbit', 'mailorbitActions']);
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { openConversation, conversationDialog } = useOpenConversation(history);

  // Liste durumu URL'den baslar (geri donuste korunur); degisince history.replace ile yazilir.
  const initial = useMemo(() => {
    const p = new URLSearchParams(location.search);
    return {
      tab: enumParam(p, 'tab', TABS, 'mine'),
      queue: p.get('queue') || '',
      page: intParam(p, 'page', 0),
      rows: intParam(p, 'rows', 25, PAGE_SIZES),
      chip: enumParam(p, 'chip', CHIPS, 'all'),
      closed: boolParam(p, 'closed', false),
      unclaimed: boolParam(p, 'unclaimed', true),
    };
    // yalnizca ilk render: sonraki URL degisiklikleri bu bilesenin kendi yazdiklaridir
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [tab, setTab] = useState(initial.tab); // 'mine' | 'queue'
  const [queues, setQueues] = useState([]);
  const [queueId, setQueueId] = useState(initial.queue);
  const [unclaimedOnly, setUnclaimedOnly] = useState(initial.unclaimed);
  const [includeClosed, setIncludeClosed] = useState(initial.closed);
  const [chip, setChip] = useState(initial.chip);
  const [sort, setSort] = useState('created'); // 'created' | 'priority'
  const [direction, setDirection] = useState('desc');
  const [page, setPage] = useState(initial.page);
  const [pageSize, setPageSize] = useState(initial.rows);
  const [reloadKey, setReloadKey] = useState(0);

  const [data, setData] = useState({ items: [], totalCount: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statuses, setStatuses] = useState({});
  const [queuesError, setQueuesError] = useState(null);
  const [claimingId, setClaimingId] = useState(null);
  const [claimError, setClaimError] = useState(null);
  // Bagli is emirleri: conversationId -> { workOrderId, isClosed } (SR-046)
  const [workOrders, setWorkOrders] = useState({});
  const [lookupLoading, setLookupLoading] = useState(false);

  // Liste durumunu URL'ye yaz (varsayilan olmayanlar)
  useEffect(() => {
    const p = new URLSearchParams();
    if (tab !== 'mine') p.set('tab', tab);
    if (queueId) p.set('queue', queueId);
    if (page > 0) p.set('page', String(page));
    if (pageSize !== 25) p.set('rows', String(pageSize));
    if (chip !== 'all') p.set('chip', chip);
    if (includeClosed) p.set('closed', '1');
    if (!unclaimedOnly) p.set('unclaimed', '0');
    const search = p.toString() ? `?${p.toString()}` : '';
    if (search !== location.search) {
      history.replace({ pathname: location.pathname, search, hash: location.hash });
    }
    // location.search bilincli olarak bagimlilik degil: yalnizca durum degisince yaz
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, queueId, page, pageSize, chip, includeClosed, unclaimedOnly]);

  // Referans veriler: kuyruklar ve status adlari
  useEffect(() => {
    let cancelled = false;
    getMyQueues()
      .then(list => {
        if (cancelled) return;
        const arr = list || [];
        setQueues(arr);
        const first = arr.find(q => q.canWork) || arr[0];
        // URL'deki kuyruk listede yoksa ilk kuyruga dus
        setQueueId(prev => (prev && arr.some(q => q.id === prev) ? prev : (first && first.id) || ''));
      })
      .catch(e => !cancelled && setQueuesError(getMailOrbitErrorKey(e)));
    getStatuses()
      .then(list => {
        if (cancelled) return;
        const map = {};
        (list || []).forEach(s => {
          map[s.id] = s;
        });
        setStatuses(map);
      })
      .catch(() => {
        // status adi gelmezse satirda genel etiket gosterilir
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Liste verisi
  useEffect(() => {
    if (tab === 'queue' && !queueId) {
      setData({ items: [], totalCount: 0 });
      return undefined;
    }
    let cancelled = false;
    const params = { skip: page * pageSize, take: pageSize, sort, direction };
    setLoading(true);
    setError(null);
    const req =
      tab === 'mine'
        ? getMyWork({ ...params, includeClosed })
        : getQueueWorkItems(queueId, { ...params, unclaimedOnly });
    req
      .then(res => {
        if (cancelled) return;
        setData({ items: (res && res.items) || [], totalCount: (res && res.totalCount) || 0 });
      })
      .catch(e => {
        if (cancelled) return;
        setData({ items: [], totalCount: 0 });
        setError(getMailOrbitErrorKey(e));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [tab, queueId, unclaimedOnly, includeClosed, sort, direction, page, pageSize, reloadKey]);

  // Is emri numaralari: yuklenen her sayfa icin tek toplu sorgu. Hata listeyi bozmaz; ek bir sey
  // gosterilmez ve bir sonraki yenilemede (data.items degisince) sessizce yeniden denenir.
  useEffect(() => {
    const ids = data.items.map(i => i.conversationId).filter(Boolean).slice(0, LOOKUP_MAX);
    if (ids.length === 0) {
      setWorkOrders({});
      setLookupLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLookupLoading(true);
    lookupWorkOrders(ids)
      .then(res => {
        if (cancelled) return;
        const map = {};
        ((res && res.items) || []).forEach(x => {
          if (x && x.conversationId && x.workOrderId) map[x.conversationId] = x;
        });
        setWorkOrders(map);
      })
      .catch(() => {
        if (!cancelled) setWorkOrders({});
      })
      .finally(() => !cancelled && setLookupLoading(false));
    return () => {
      cancelled = true;
    };
  }, [data.items]);

  const resetPage = () => setPage(0);

  const handleSort = column => {
    if (sort === column) {
      setDirection(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(column);
      setDirection('desc');
    }
    resetPage();
  };

  const isOpenItem = item => {
    const s = statuses[item.statusDefinitionId];
    return !(s && s.isTerminal);
  };

  // Hizli filtre: gecerli sayfadaki satirlara uygulanir (API cagrisi degismez).
  const visibleItems = useMemo(() => {
    if (chip === 'open') return data.items.filter(isOpenItem);
    if (chip === 'high') {
      return data.items.filter(i => i.priority === 'High' || i.priority === 'Urgent');
    }
    return data.items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chip, data.items, statuses]);

  const openItem = item =>
    openConversation({
      conversationId: item.conversationId,
      caseReference: item.caseReference,
    });

  const handleClaim = useCallback(
    async (event, item) => {
      event.stopPropagation(); // satir acilmasin
      if (claimingId) return;
      setClaimingId(item.conversationId);
      setClaimError(null);
      try {
        await claimConversation(item.conversationId);
        setClaimingId(null);
        openItem(item);
      } catch (e) {
        setClaimingId(null);
        setClaimError(
          isMailOrbitError(e) && e.kind === 'conflict'
            ? t('mailorbitActions:claim_conflict')
            : t(`mailorbit:${getMailOrbitErrorKey(e)}`)
        );
        setReloadKey(k => k + 1); // satiri tazele
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [claimingId, openConversation, t]
  );

  const statusCell = item => {
    const s = statuses[item.statusDefinitionId];
    if (!s) return <Chip size="small" label={t('status_unknown')} />;
    let color = OK;
    if (s.isTerminal) color = IDLE;
    else if (/wait|pend/i.test(s.key || '')) color = WARN;
    return (
      <Typography variant="body2" component="span">
        <span className={classes.dot} style={{ backgroundColor: color }} />
        {s.name}
      </Typography>
    );
  };

  const workOrderCell = item => {
    const link = workOrders[item.conversationId];
    if (link) {
      return (
        <>
          <Link
            component="button"
            type="button"
            className={classes.workOrderLink}
            onClick={e => {
              e.stopPropagation(); // satir acilmasin
              openWorkOrder(history, link.workOrderId);
            }}
            onKeyDown={e => e.stopPropagation()}
          >
            {link.workOrderId}
          </Link>
          {link.isClosed ? <span className={classes.closedMark}>{t('work_order_closed')}</span> : null}
        </>
      );
    }
    if (lookupLoading) return <Skeleton width={60} />;
    return <span className={classes.muted}>-</span>;
  };

  const priorityClass = priority => {
    if (priority === 'Urgent') return classes.pillUrgent;
    if (priority === 'High') return classes.pillHigh;
    return '';
  };

  const priorityCell = item => (
    <Chip
      size="small"
      label={t(`priority_${item.priority}`, item.priority || '-')}
      className={`${classes.pill} ${priorityClass(item.priority)}`}
    />
  );

  const selectedQueue = queues.find(q => q.id === queueId);
  let emptyKey = tab === 'mine' ? 'empty_mine' : 'empty_queue';
  if (chip !== 'all' && data.items.length > 0) emptyKey = 'empty_filtered';
  const showSkeleton = loading && data.items.length === 0;
  const refreshing = loading && data.items.length > 0;

  return (
    <Page title={t('page_title')}>
      <Container maxWidth={false} className={classes.page} style={{ paddingTop: 16, paddingBottom: 16 }}>
        <Breadcrumbs className={classes.crumbs}>
          <Link color="inherit">Sirius</Link>
          <Typography variant="inherit">{t('page_title')}</Typography>
        </Breadcrumbs>
        <Card className={classes.card}>
          <div className={classes.toolbar}>
            <Tabs
              value={tab}
              onChange={(e, v) => {
                setTab(v);
                resetPage();
              }}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab className={classes.tab} value="mine" label={t('tab_mine')} />
              <Tab className={classes.tab} value="queue" label={t('tab_queue')} />
            </Tabs>
            <span className={classes.spacer} />
            <AvailabilityControl onChanged={() => setReloadKey(k => k + 1)} />
          </div>
          <CardContent style={{ padding: 0 }}>
            <Box className={classes.controls}>
              {tab === 'queue' ? (
                <>
                  <FormControl variant="outlined" size="small" style={{ minWidth: 240 }}>
                    <InputLabel id="mo-queue-label">{t('queue_label')}</InputLabel>
                    <Select
                      labelId="mo-queue-label"
                      label={t('queue_label')}
                      value={queueId}
                      onChange={e => {
                        setQueueId(e.target.value);
                        resetPage();
                      }}
                    >
                      {queues.map(q => (
                        <MenuItem key={q.id} value={q.id}>
                          {t('queue_option', {
                            name: q.name,
                            waiting: q.unclaimedWorkItemCount,
                          })}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={(
                      <Switch
                        color="primary"
                        checked={unclaimedOnly}
                        onChange={e => {
                          setUnclaimedOnly(e.target.checked);
                          resetPage();
                        }}
                      />
                    )}
                    label={t('unclaimed_only')}
                  />
                </>
              ) : (
                <FormControlLabel
                  control={(
                    <Switch
                      color="primary"
                      checked={includeClosed}
                      onChange={e => {
                        setIncludeClosed(e.target.checked);
                        resetPage();
                      }}
                    />
                  )}
                  label={t('include_closed')}
                />
              )}
              <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
                <InputLabel id="mo-sort-label">{t('sort_label')}</InputLabel>
                <Select
                  labelId="mo-sort-label"
                  label={t('sort_label')}
                  value={sort}
                  onChange={e => {
                    if (SORTS.includes(e.target.value)) {
                      setSort(e.target.value);
                      setDirection('desc');
                      resetPage();
                    }
                  }}
                >
                  <MenuItem value="created">{t('sort_created')}</MenuItem>
                  <MenuItem value="priority">{t('sort_priority')}</MenuItem>
                </Select>
              </FormControl>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => setReloadKey(k => k + 1)}
                disabled={loading}
              >
                {t('btn_refresh')}
              </Button>
            </Box>

            <div className={classes.filters} role="group" aria-label={t('filter_label')}>
              {CHIPS.map(c => (
                <Chip
                  key={c}
                  size="small"
                  label={t(`chip_${c}`)}
                  className={`${classes.chip} ${chip === c ? classes.chipOn : ''}`}
                  clickable
                  aria-pressed={chip === c}
                  onClick={() => setChip(c)}
                />
              ))}
            </div>

            {tab === 'queue' && selectedQueue && !selectedQueue.canWork ? (
              <Alert severity="info" style={{ marginBottom: 8 }}>
                {t('queue_cannot_work')}
              </Alert>
            ) : null}
            {queuesError ? (
              <Alert severity="error" style={{ marginBottom: 8 }}>
                {t(`mailorbit:${queuesError}`)}
              </Alert>
            ) : null}
            {error ? (
              <Alert
                severity="error"
                style={{ marginBottom: 8 }}
                action={(
                  <Button color="inherit" size="small" onClick={() => setReloadKey(k => k + 1)}>
                    {t('btn_retry')}
                  </Button>
                )}
              >
                {t(`mailorbit:${error}`)}
              </Alert>
            ) : null}

            <TableContainer className={classes.tableContainer}>
              <Table size="small" stickyHeader aria-busy={loading}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.th}>{t('col_case')}</TableCell>
                    <TableCell className={classes.th}>{t('col_work_order')}</TableCell>
                    <TableCell className={classes.th}>{t('col_subject_sender')}</TableCell>
                    <TableCell className={classes.th}>{t('col_status')}</TableCell>
                    <TableCell className={classes.th} sortDirection={sort === 'priority' ? direction : false}>
                      <TableSortLabel
                        active={sort === 'priority'}
                        direction={sort === 'priority' ? direction : 'desc'}
                        onClick={() => handleSort('priority')}
                      >
                        {t('col_priority')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.th}>{t('col_last_message')}</TableCell>
                    <TableCell className={classes.th} />
                  </TableRow>
                </TableHead>
                <TableBody className={refreshing ? classes.refreshing : undefined}>
                  {showSkeleton
                    ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                      <TableRow key={`sk-${i}`}>
                        <TableCell>
                          <Skeleton width={60} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={60} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width="70%" />
                          <Skeleton width="40%" />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={70} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={60} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={70} />
                        </TableCell>
                        <TableCell />
                      </TableRow>
                      ))
                    : visibleItems.map(item => {
                        const masked = item.isMasked;
                        const claimable =
                          tab === 'queue' &&
                          !item.currentOwnerUserId &&
                          !(selectedQueue && !selectedQueue.canWork);
                        const last = item.lastMessageAtUtc;
                        return (
                          <TableRow
                            key={item.conversationId}
                            className={classes.row}
                            role="link"
                            tabIndex={0}
                            onClick={() => openItem(item)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' && e.target === e.currentTarget) {
                                e.preventDefault();
                                openItem(item);
                              }
                            }}
                          >
                            <TableCell className={`${classes.td} ${classes.muted}`}>{item.caseReference}</TableCell>
                            <TableCell className={classes.td}>{workOrderCell(item)}</TableCell>
                            <TableCell className={classes.td}>
                              <Typography
                                variant="body2"
                                component="span"
                                className={classes.subject}
                              >
                                {masked || !item.subject ? <em>{t('masked')}</em> : item.subject}
                                {item.hasAttachments ? (
                                  <AttachFileIcon
                                    fontSize="inherit"
                                    style={{ verticalAlign: 'middle', marginLeft: 4 }}
                                  />
                                ) : null}
                                {item.hasPendingRerouteReview ? (
                                  <Chip
                                    size="small"
                                    style={{ marginLeft: 8 }}
                                    label={t('reroute_pending')}
                                  />
                                ) : null}
                              </Typography>
                              <span className={classes.sender}>
                                {masked ? (
                                  <em>{t('masked')}</em>
                                ) : (
                                  item.senderName || item.senderAddress || ''
                                )}
                              </span>
                            </TableCell>
                            <TableCell className={classes.td}>{statusCell(item)}</TableCell>
                            <TableCell className={classes.td}>{priorityCell(item)}</TableCell>
                            <TableCell className={`${classes.td} ${classes.muted}`}>
                              <Tooltip
                                title={last ? moment(last).format('DD.MM.YYYY HH:mm') : ''}
                              >
                                <Typography variant="body2" component="span">
                                  {formatRelative(last)}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell className={`${classes.td} ${classes.go}`}>
                              {claimable ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  disabled={!!claimingId}
                                  onClick={e => handleClaim(e, item)}
                                  onKeyDown={e => e.stopPropagation()}
                                  style={{ marginRight: 8 }}
                                >
                                  {t('btn_claim')}
                                </Button>
                              ) : null}
                              <ChevronRightIcon className={classes.chevron} aria-hidden="true" />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  {!loading && !error && visibleItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography variant="body2" className={classes.empty} color="textSecondary">
                          {t(emptyKey)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={data.totalCount}
              page={page}
              rowsPerPage={pageSize}
              rowsPerPageOptions={PAGE_SIZES}
              onChangePage={(e, p) => setPage(p)}
              onChangeRowsPerPage={e => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage={t('rows_per_page')}
              className={classes.pager}
            />
          </CardContent>
        </Card>
      </Container>
      <Snackbar
        open={!!claimError}
        autoHideDuration={6000}
        onClose={() => setClaimError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setClaimError(null)}>
          {claimError}
        </Alert>
      </Snackbar>
      {conversationDialog}
    </Page>
  );
};

/**
 * "Email islerim" sayfasi. Gate kapali tenant'larda sayfa render edilmez ve hicbir MailOrbit
 * istegi yapilmaz; kullanici ana sayfaya yonlendirilir.
 */
const MyEmailWork = () => {
  const { enabled, loaded } = useMailOrbitFeature();
  if (!loaded) return null; // kapı yüklenirken ne içerik ne yönlendirme, istek yok
  if (!enabled) {
    return <Redirect to="/overview" />;
  }
  return <MyEmailWorkContent />;
};

export default MyEmailWork;
